import type { Client, RoomSummary, Unsubscribe } from "~/lib/matrix";

export const SIDEBAR_LAYOUT_TYPE = "com.nurion.sidebar.v1" as const;

/** The sections every surface shows. */
export type CoreSectionId = "channels" | "dms";
/** Desktop also orders its sub-space sections, as `space:<roomId>`. */
export type SidebarSectionId = CoreSectionId | `space:${string}`;

export interface SidebarLayout {
  order: SidebarSectionId[];
  hidden: string[];
}

/** Mobile's Slack default: channels, then people. */
export const MOBILE_SECTION_ORDER: readonly CoreSectionId[] = ["channels", "dms"];

export const DEFAULT_SIDEBAR_LAYOUT: SidebarLayout = {
  order: [...MOBILE_SECTION_ORDER],
  hidden: [],
};

/** Desktop lists people first; mobile keeps DEFAULT_SIDEBAR_LAYOUT until the user reorders. */
export const DESKTOP_SECTION_ORDER: readonly CoreSectionId[] = ["dms", "channels"];

export function isCoreSection(value: unknown): value is CoreSectionId {
  return value === "channels" || value === "dms";
}

function isSectionId(value: unknown): value is SidebarSectionId {
  return isCoreSection(value) || (typeof value === "string" && /^space:!\S+$/.test(value));
}

export function normaliseSidebarLayout(
  raw: unknown,
  defaultOrder: readonly CoreSectionId[] = MOBILE_SECTION_ORDER,
): SidebarLayout {
  if (!raw || typeof raw !== "object") return { order: [...defaultOrder], hidden: [] };
  const input = raw as { order?: unknown; hidden?: unknown };
  const order = Array.isArray(input.order)
    ? Array.from(new Set(input.order.filter(isSectionId)))
    : [];
  for (const id of defaultOrder) if (!order.includes(id)) order.push(id);
  const hidden = Array.isArray(input.hidden)
    ? Array.from(new Set(input.hidden.filter((id): id is string => typeof id === "string")))
    : [];
  return { order, hidden };
}

export function subscribeSidebarLayout(
  client: Client,
  cb: (layout: SidebarLayout) => void,
  defaultOrder?: readonly CoreSectionId[],
): Unsubscribe {
  return client.bindings.subscribeAccountData<unknown>(SIDEBAR_LAYOUT_TYPE, (raw) => {
    cb(normaliseSidebarLayout(raw, defaultOrder));
  });
}

export async function writeSidebarLayout(client: Client, layout: SidebarLayout): Promise<void> {
  await client.bindings.setAccountData<SidebarLayout>(SIDEBAR_LAYOUT_TYPE, layout);
}

/** Moves a core section one step up (`-1`) or down (`1`) past the other core section. */
export function moveSection(layout: SidebarLayout, id: CoreSectionId, step: -1 | 1): SidebarLayout {
  const core = layout.order.filter(isCoreSection);
  const neighbour = core[core.indexOf(id) + step];
  if (!core.includes(id) || !neighbour) return layout;
  const order = layout.order.map((entry) =>
    entry === id ? neighbour : entry === neighbour ? id : entry,
  );
  return { ...layout, order };
}

/**
 * The present sections in display order: saved ids first, then each new
 * section at its default slot (after the nearest default predecessor already
 * placed), so a new sub-space lands next to its siblings, not at the end.
 */
export function applySectionOrder(
  saved: readonly SidebarSectionId[],
  defaults: readonly SidebarSectionId[],
): SidebarSectionId[] {
  const present = new Set(defaults);
  const out = saved.filter((id, i) => present.has(id) && saved.indexOf(id) === i);
  defaults.forEach((id, i) => {
    if (out.includes(id)) return;
    let at = 0;
    for (let j = i - 1; j >= 0; j--) {
      const index = out.indexOf(defaults[j]!);
      if (index >= 0) {
        at = index + 1;
        break;
      }
    }
    out.splice(at, 0, id);
  });
  return out;
}

/** Stores a new visible order while keeping saved ids this surface isn't showing. */
export function withSectionOrder(
  layout: SidebarLayout,
  visible: readonly SidebarSectionId[],
): SidebarLayout {
  const rest = layout.order.filter((id) => !visible.includes(id));
  return { ...layout, order: [...visible, ...rest] };
}

export function setRoomHidden(
  layout: SidebarLayout,
  roomId: string,
  hidden: boolean,
): SidebarLayout {
  const rest = layout.hidden.filter((id) => id !== roomId);
  return { ...layout, hidden: hidden ? [...rest, roomId] : rest };
}

/** A hidden room returns to the list while it holds a mention for you. */
export function isRoomHidden(layout: SidebarLayout, room: RoomSummary): boolean {
  return room.unreadHighlight === 0 && layout.hidden.includes(room.id);
}

/**
 * Keeps one row per 1:1 DM partner: the first (most recent, given a
 * recency-sorted input) wins. A duplicate that still holds unread
 * messages stays so nothing unread disappears. A DM the partner has left
 * (no partner id) is never folded by display name, so two different
 * people of the same name stay distinct.
 */
export function foldDirectDuplicates(rooms: RoomSummary[]): RoomSummary[] {
  const seen = new Set<string>();
  return rooms.filter((room) => {
    if (!room.isDirect) return true;
    // Unread always shows; record the partner so a later read duplicate
    // of the same partner still folds instead of opening a second row.
    if (room.unreadTotal > 0) {
      if (room.directUserIds.length === 1) seen.add(room.directUserIds[0]!);
      return true;
    }
    // Without a single partner id there is nothing safe to match on.
    if (room.directUserIds.length !== 1) return true;
    const partner = room.directUserIds[0]!;
    if (seen.has(partner)) return false;
    seen.add(partner);
    return true;
  });
}
